import React, { useEffect, useRef } from 'react';
import { X, ChevronRight, ArrowRight, ScanSearch, CloudCog, FileText, Users, Briefcase, Cloud, Layers, Sparkles } from 'lucide-react';

const SERVICE_ICONS = {
  'cloud-strategy': Cloud,
  'delivery-ops': Layers,
  'ai-solutions': Sparkles,
};

/**
 * SLS-style full-width mega panel that drops under the header (not a side drawer).
 */
export default function NavMegaPanel({
  type,
  content,
  caseStudies,
  workItems,
  onClose,
  onNavigateToService,
  onContact,
  onOpenMega,
}) {
  const panelRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !e.target.closest('[data-mega-trigger]')) {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onClose]);

  const go = (hash) => {
    onClose();
    if (hash) window.location.hash = hash;
  };

  return (
    <>
      <div className="fixed inset-0 top-[65px] z-[180] bg-black/40 backdrop-blur-[2px]" aria-hidden="true" />
      <div
        ref={panelRef}
        className="absolute left-0 right-0 top-full z-[190] border-b shadow-2xl animate-[jbMegaIn_180ms_ease-out]"
        style={{
          background: 'var(--jb-forest-bg)',
          borderColor: 'var(--jb-border)',
          boxShadow: '0 24px 48px rgba(0,0,0,0.35)',
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-8 md:py-10">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <p className="font-mono text-[0.65rem] text-cyan uppercase tracking-widest mb-1">
                {type === 'services' && 'Capabilities'}
                {type === 'products' && 'Platforms'}
                {type === 'casestudies' && 'Insights'}
                {type === 'about' && 'Company'}
              </p>
              <h2 className="font-display font-extrabold text-xl sm:text-2xl text-text-hero">
                {type === 'services' && (content?.servicesOverlay?.heading || 'Services')}
                {type === 'products' && (content?.products?.heading || 'Products')}
                {type === 'casestudies' && 'Case Studies'}
                {type === 'about' && 'Who We Are'}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-text-sub hover:text-cyan transition"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* SERVICES — multi-column like SLS */}
          {type === 'services' && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {(content?.servicesOverlay?.groups || []).map((group) => {
                const Icon = SERVICE_ICONS[group.id] || Layers;
                return (
                  <div key={group.id}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-4 h-4 text-cyan" />
                      <h3 className="font-display font-bold text-text-hero text-sm uppercase tracking-wide">
                        {group.name}
                      </h3>
                    </div>
                    {group.description && (
                      <p className="font-body text-xs text-text-sub mb-3 leading-relaxed">{group.description}</p>
                    )}
                    <ul className="space-y-1">
                      {(group.items || []).map((item) => (
                        <li key={item.slug}>
                          <button
                            type="button"
                            onClick={() => {
                              onClose();
                              if (onNavigateToService) onNavigateToService(item.slug);
                              else window.location.hash = `#service/${item.slug}`;
                            }}
                            className="group w-full text-left flex items-center justify-between gap-2 py-2 px-2 -mx-2 rounded-lg hover:bg-cyan/5 transition"
                          >
                            <span>
                              <span className="font-body text-sm text-text-body group-hover:text-cyan transition block">
                                {item.title}
                              </span>
                              {item.shortDescription && (
                                <span className="font-body text-xs text-text-muted line-clamp-1">
                                  {item.shortDescription}
                                </span>
                              )}
                            </span>
                            <ChevronRight className="w-4 h-4 text-cyan opacity-0 group-hover:opacity-100 flex-shrink-0" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}

          {/* PRODUCTS */}
          {type === 'products' && (
            <div className="grid md:grid-cols-2 gap-5">
              {(content?.products?.items || []).map((item) => {
                const Icon = item.slug === 'omnilens' ? CloudCog : ScanSearch;
                return (
                  <button
                    key={item.slug}
                    type="button"
                    onClick={() => go(`#product/${item.slug}`)}
                    className="group text-left p-5 rounded-xl border bg-forest-card hover:border-cyan/50 transition"
                    style={{ borderColor: 'var(--jb-border)' }}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: 'var(--jb-cyan-dim)' }}
                      >
                        <Icon className="w-5 h-5 text-cyan" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-display font-bold text-text-hero group-hover:text-cyan transition">
                            {item.title}
                          </span>
                          {item.badge && (
                            <span className="font-mono text-[0.6rem] uppercase tracking-wider text-cyan">
                              {item.badge}
                            </span>
                          )}
                        </div>
                        <p className="font-body text-sm text-text-sub leading-relaxed line-clamp-2">
                          {item.shortDescription}
                        </p>
                        <span className="inline-flex items-center gap-1 mt-3 text-cyan text-sm font-semibold">
                          Explore <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* CASE STUDIES */}
          {type === 'casestudies' && (
            <div>
              <div className="grid sm:grid-cols-2 gap-3 mb-6">
                {(caseStudies || []).slice(0, 6).map((item) => (
                  <button
                    key={item.slug}
                    type="button"
                    onClick={() => go(`#casestudy/${item.slug}`)}
                    className="group text-left p-4 rounded-xl border hover:border-cyan/50 transition flex items-start gap-3"
                    style={{ borderColor: 'var(--jb-border)', background: 'var(--jb-list-item-bg)' }}
                  >
                    <FileText className="w-4 h-4 text-cyan mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-display font-bold text-sm text-text-hero group-hover:text-cyan transition block">
                        {item.title}
                      </span>
                      <span className="font-body text-xs text-text-sub line-clamp-2 mt-0.5 block">
                        {item.shortDescription}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => go('#casestudies')}
                className="inline-flex items-center gap-2 font-body font-semibold text-cyan text-sm"
              >
                View all case studies <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* WHO WE ARE */}
          {type === 'about' && (
            <div className="space-y-8">
              <div className="grid sm:grid-cols-3 gap-5">
                {[
                  { icon: Users, title: 'About JungleBrick', desc: 'Mission, story, and how we work.', hash: '#about' },
                  { icon: Briefcase, title: 'Our Work', desc: 'Client delivery and engagements.', hash: '#work' },
                  { icon: ScanSearch, title: 'Products', desc: 'PromptLens & OmniLens.', hash: null, mega: 'products' },
                ].map((row) => (
                  <button
                    key={row.title}
                    type="button"
                    onClick={() => {
                      if (row.mega && onOpenMega) {
                        onOpenMega(row.mega);
                        return;
                      }
                      go(row.hash);
                    }}
                    className="group text-left p-5 rounded-xl border bg-forest-card hover:border-cyan/50 transition"
                    style={{ borderColor: 'var(--jb-border)' }}
                  >
                    <row.icon className="w-5 h-5 text-cyan mb-3" />
                    <h3 className="font-display font-bold text-text-hero group-hover:text-cyan transition mb-1">
                      {row.title}
                    </h3>
                    <p className="font-body text-sm text-text-sub">{row.desc}</p>
                  </button>
                ))}
              </div>

              {(workItems || []).length > 0 && (
                <div>
                  <p className="font-mono text-[0.65rem] text-cyan uppercase tracking-widest mb-3">
                    Featured engagements
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {workItems.map((item) => (
                      <button
                        key={item.slug}
                        type="button"
                        onClick={() => go(`#project/${item.slug}`)}
                        className="group text-left p-4 rounded-xl border hover:border-cyan/50 transition"
                        style={{ borderColor: 'var(--jb-border)', background: 'var(--jb-list-item-bg)' }}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="font-display font-bold text-sm text-text-hero group-hover:text-cyan transition">
                            {item.title}
                          </span>
                          <ChevronRight className="w-4 h-4 text-cyan opacity-0 group-hover:opacity-100" />
                        </div>
                        {item.badge && (
                          <span className="font-mono text-[0.6rem] text-cyan uppercase tracking-wider">
                            {item.badge}
                          </span>
                        )}
                        <p className="font-body text-xs text-text-sub mt-1.5 line-clamp-2">
                          {item.description}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-8 pt-6 border-t flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" style={{ borderColor: 'var(--jb-border)' }}>
            <p className="font-body text-sm text-text-sub max-w-md">
              Ready to talk cloud, AI, or FinOps? We respond within one business day.
            </p>
            <button
              type="button"
              onClick={() => {
                onClose();
                if (onContact) onContact();
              }}
              className="jb-cta inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan to-accent-green text-forest-bg font-body font-bold text-sm hover:opacity-90 transition"
            >
              Book a discovery call <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes jbMegaIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
